import React, { useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, CardText, Carousel, CarouselItem, CarouselControl, Spinner } from "reactstrap";
import Icon from "../../components/icon/Icon";
import { Block, BlockHead, BlockHeadContent, BlockTitle } from "../../components/block/Block";
import Content from "../../layout/content/Content";
import { Link } from "react-router-dom";
import Head from "../../layout/head/Head";
import LogoSSB from "../../images/ssb_white.158c7b19.png";
import "./MetricsHome.css";
import Aviso from "./AlertasDashboard/Aviso";

// ─── Helpers de rol (mismo criterio que MenuData.js y RolMetricProtectedRoute) ──
const ROLE_SECTIONS = {
  'maestro':      ['imports', 'exports', 'mro', 'dispatches', 'remitos', 'tools'],
  'general':      ['imports', 'exports', 'mro', 'dispatches'],
  'import':       ['imports'],
  'import admin': ['imports', 'tools'],
  'export':       ['exports'],
  'export admin': ['exports', 'tools'],
  'mro':          ['mro'],
  'mro admin':    ['mro', 'tools'],
  'remitos':      ['remitos'],
  'appo':         ['appo'],
};

const getCurrentDatabase = () => {
  try { return localStorage.getItem('currentDatabase') || ''; } catch { return ''; }
};

const getCurrentUserRolMetric = () => {
  try {
    const raw = localStorage.getItem('user');
    if (raw) return JSON.parse(raw)?.rol_metric_name || null;
  } catch { /* ignore */ }
  return null;
};

const canSeeSection = (section) => {
  const isDOW     = getCurrentDatabase() === 'ssb_internacional';
  const rolMetric = getCurrentUserRolMetric();
  if (!isDOW || !rolMetric) return true;
  const allowed = ROLE_SECTIONS[rolMetric.toLowerCase()];
  if (!allowed) return true;
  return allowed.includes(section);
};

// ─── Definición de módulos (orden: imports → exports → mro → dispatches → tools) ──
const ALL_FEATURES = [
  {
    section: 'imports',
    title: "Imports Management",
    description: "Complete import operations management with real-time tracking, automated documentation, and comprehensive reporting.",
    icon: "inbox-in",
    color: "primary",
    features: [
      "Follow-UP tracking in real-time",
      "New Orders registration",
      "Automatization Gestor",
      "APPO Mails management"
    ],
    link: "/listplasticos"
  },
  {
    section: 'exports',
    title: "Exports Management",
    description: "End-to-end export operations control with benefits tracking, master database, and automated document management.",
    icon: "inbox-out",
    color: "success",
    features: [
      "Export Follow-UP system",
      "Benefits tracking & reintegros",
      "Master database management",
      "Automatization Gestor",
      "Performance analytics"
    ],
    link: "/followUpExpo"
  },
  {
    section: 'mro',
    title: "MRO Operations",
    description: "Maintenance, Repair, and Operations management with temporary imports control and payments tracking.",
    icon: "package",
    color: "purple",
    features: [
      "Temporary imports management",
      "Payments upload & tracking",
      "MRO documentation control",
      "Compliance monitoring"
    ],
    link: "/temporalesMro"
  },
  {
    section: 'dispatches',
    title: "Dispatches",
    description: "Real-time dispatches tracking with comprehensive query system and delivery status monitoring.",
    icon: "truck",
    color: "info",
    features: [
      "Dispatches Query interface",
      "Real-time status tracking",
      "Transit alerts & notifications",
      "Delivery confirmations"
    ],
    link: "/interlogApi"
  },
  {
    section: 'remitos',
    title: "Remitos",
    description: "Internal remittance management with issuance, listing and configuration tools for streamlined document control.",
    icon: "file-docs",
    color: "danger",
    features: [
      "Remitos Altas ",
      "Remitos Listado ",
      "Config. Punto de Venta",
      "Config. Cajas",
    ],
    link: "/remitos/altas"
  },
  {
    section: 'appo',
    title: "APPO Operations",
    description: "Isotank and cylinder management with cleaning operations, shipper own inventory, and mail communications.",
    icon: "box",
    color: "info",
    features: [
      "Isotank To Clean - Pendientes",
      "Isotank To Clean - Gestion",
      "Isotank Shipper Own Gestion",
      "Cylinders Temporary Admission",
      "Gestión de Mails APPO"
    ],
    link: "/dashboardAppo"
  },
  {
    section: 'tools',
    title: "Tools & Administration",
    description: "Administrative and configuration tools to manage users, cargo routes, global updates and platform-wide automation.",
    icon: "setting",
    color: "warning",
    features: [
      "Users & Permissions management",
      "Cargo Routes configuration",
      "Platform settings",
      "API & automation updates"
    ],
    link: "/users-admin"
  },
];

// La clave se deriva del auth_token, que cambia en cada login y se borra en logout.
// Se guarda en localStorage (no sessionStorage) para sobrevivir recargas,
// pero la clave cambia con cada sesión → el aviso aparece exactamente una vez por login.
const getAvisoKey = () => {
  try {
    const token = localStorage.getItem('auth_token') || '';
    if (!token) return null;
    // Últimos 24 chars del token — suficiente para discriminar sin exponer el token completo
    return `aviso_shown_${token.slice(-24)}`;
  } catch { return null; }
};

const MetricsHome = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animating, setAnimating] = useState(false);
  const itemsPerSlide = 3;

  // ── Aviso automático (una vez por sesión de login) ────────────────
  const rolMetric = (getCurrentUserRolMetric() || '').toLowerCase().trim();
  const isDOW = getCurrentDatabase() === 'ssb_internacional';
  const avisoKey = getAvisoKey();
  const shouldShowAviso = isDOW && rolMetric !== 'remitos' && rolMetric !== 'appo' && !!avisoKey && !localStorage.getItem(avisoKey);
  const [avisoOverlay, setAvisoOverlay] = useState(shouldShowAviso);

  const handleAvisoOpen = () => {
    setAvisoOverlay(false);
  };

  const handleAvisoDismiss = () => {
    if (avisoKey) localStorage.setItem(avisoKey, '1');
  };

  // Filtrar módulos según el rol del usuario logueado
  const systemFeatures = useMemo(
    () => ALL_FEATURES.filter(f => canSeeSection(f.section)),
    []
  );

  const maxSlides = Math.ceil(systemFeatures.length / itemsPerSlide);

  const next = () => {
    if (animating) return;
    const nextIndex = activeIndex === maxSlides - 1 ? 0 : activeIndex + 1;
    setActiveIndex(nextIndex);
  };

  const previous = () => {
    if (animating) return;
    const nextIndex = activeIndex === 0 ? maxSlides - 1 : activeIndex - 1;
    setActiveIndex(nextIndex);
  };

  const goToIndex = (newIndex) => {
    if (animating) return;
    setActiveIndex(newIndex);
  };

  const createSlides = () => {
    const slides = [];
    for (let i = 0; i < maxSlides; i++) {
      const startIndex = i * itemsPerSlide;
      const endIndex = Math.min(startIndex + itemsPerSlide, systemFeatures.length);
      const slideFeatures = systemFeatures.slice(startIndex, endIndex);
      
      slides.push(
        <CarouselItem
          onExiting={() => setAnimating(true)}
          onExited={() => setAnimating(false)}
          key={i}
        >
          <div className="py-3">
            <Row className="g-5 justify-content-center" style={{ padding: '0 15px' }}>
              {slideFeatures.map((feature, index) => (
              <Col key={feature.title} lg={4} md={6} sm={12}>
                <Card className="h-100 border-0 shadow-sm hover-shadow feature-card" style={{ transition: 'all 0.3s ease', borderRadius: '20px', overflow: 'hidden' }}>
                  <CardBody className="text-center p-4 d-flex flex-column" style={{ position: 'relative', height: '100%' }}>
                    <div className={`icon-circle icon-circle-xxl mx-auto mb-4`} style={{ 
                      borderRadius: '50%', 
                      padding: '25px', 
                      width: '100px', 
                      height: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 15px 35px rgba(0,0,0,0.15)`,
                      background: feature.color === 'primary' 
                        ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)'
                        : feature.color === 'success'
                        ? 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                        : feature.color === 'warning'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : feature.color === 'purple'
                        ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
                        : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', // info color
                      border: '3px solid rgba(255,255,255,0.2)'
                    }}>
                      <Icon name={feature.icon} className="text-white" style={{ 
                        fontSize: '3rem', 
                        filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))',
                        fontWeight: 'bold'
                      }} />
                    </div>
                    <CardTitle tag="h5" className="mb-3 fw-bold" style={{ color: '#2c3e50', fontSize: '1.4rem' }}>
                      {feature.title}
                    </CardTitle>
                    <CardText className="text-muted mb-4" style={{ fontSize: '1rem', lineHeight: '1.7', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {feature.description}
                    </CardText>
                    <div className="mb-4 flex-grow-1" style={{ height: '160px', overflow: 'hidden' }}>
                      <ul className="list-unstyled text-start" style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
                        {feature.features.map((item, itemIndex) => (
                          <li key={itemIndex} className="mb-3 d-flex align-items-center" style={{ fontSize: '0.95rem' }}>
                            <div className="me-3 flex-shrink-0" style={{
                              width: '22px',
                              height: '22px',
                              borderRadius: '50%',
                              background: feature.color === 'primary' 
                                ? 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)'
                                : feature.color === 'success'
                                ? 'linear-gradient(135deg, #10b981 0%, #047857 100%)'
                                : feature.color === 'warning'
                                ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                : feature.color === 'purple'
                                ? 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)'
                                : 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', // info color
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: `0 3px 8px ${
                                feature.color === 'primary' 
                                  ? 'rgba(59, 130, 246, 0.4)'
                                  : feature.color === 'success'
                                  ? 'rgba(16, 185, 129, 0.4)'
                                  : feature.color === 'warning'
                                  ? 'rgba(245, 158, 11, 0.4)'
                                  : feature.color === 'purple'
                                  ? 'rgba(139, 92, 246, 0.4)'
                                  : 'rgba(6, 182, 212, 0.4)' // info color
                              }`,
                              border: '2px solid rgba(255,255,255,0.3)'
                            }}>
                              <Icon name="check" className="text-white" style={{ 
                                fontSize: '0.9rem',
                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))',
                                fontWeight: 'bold'
                              }} />
                            </div>
                            <span style={{ fontWeight: '500', marginLeft: '5px' }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-auto">
                      <Link to={feature.link} className={`btn btn-${feature.color} btn-round px-5 py-3 fw-semibold`} style={{ 
                        textDecoration: 'none',
                        fontSize: '1rem',
                        borderRadius: '25px',
                        boxShadow: `0 8px 25px rgba(0,0,0,0.15)`,
                        transform: 'translateY(0)',
                        transition: 'all 0.3s ease'
                      }}>
                        Access Module
                      </Link>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
          </div>
        </CarouselItem>
      );
    }
    return slides;
  };

  const slides = createSlides();

  return (
    <React.Fragment>
      {/* ── Overlay spinner (primera visita por sesión) ─────────────── */}
      {avisoOverlay && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(15, 23, 42, 0.55)",
          backdropFilter: "blur(3px)",
          display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center",
          gap: 16,
        }}>
          <Spinner style={{ width: 52, height: 52, borderWidth: 5, color: "#f6851f" }} />
          <span style={{ color: "rgba(255,255,255,.85)", fontSize: ".95rem", fontWeight: 600, letterSpacing: ".02em" }}>
            Cargando alertas…
          </span>
        </div>
      )}

      {/* ── Aviso automático ─────────────────────────────────────────── */}
      {shouldShowAviso && (
        <Aviso
          userRole={rolMetric}
          autoOpen
          onOpen={handleAvisoOpen}
          onDismiss={handleAvisoDismiss}
        />
      )}

      <Head title="Home" />
      <Content>

        {/* ── DOW Dashboard quick-access banner (only for DOW company) ── */}
        {/*isDOW && (
          <Block className="mb-4">
            <Card className="border-0 shadow-sm" style={{
              borderRadius: '20px',
              background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* decorative glow *//*}
              <div style={{
                position: 'absolute', top: '-40px', right: '-40px',
                width: '180px', height: '180px',
                background: 'radial-gradient(circle, rgba(59,130,246,0.35) 0%, transparent 70%)',
                borderRadius: '50%', pointerEvents: 'none',
              }} />
              <CardBody className="py-4 px-5">
                <Row className="align-items-center">
                  <Col xs="auto">
                    <div style={{
                      width: '56px', height: '56px', borderRadius: '14px',
                      background: 'rgba(255,255,255,0.12)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: '1.5px solid rgba(255,255,255,0.25)',
                    }}>
                      <Icon name="dashboard-fill" className="text-white" style={{ fontSize: '1.8rem' }} />
                    </div>
                  </Col>
                  <Col>
                    <h5 className="text-white fw-bold mb-1" style={{ fontSize: '1.15rem', letterSpacing: '0.01em' }}>
                      DOW Operations Dashboard
                    </h5>
                    <p className="mb-0" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem' }}>
                      Imports · Exports · MRO · Email automation — all in one view
                    </p>
                  </Col>
                  <Col xs="auto">
                    <Link
                      to="/dow-dashboard"
                      className="btn btn-light fw-semibold"
                      style={{ borderRadius: '12px', fontSize: '0.9rem', padding: '0.5rem 1.4rem' }}
                    >
                      <Icon name="arrow-right" /> &nbsp;Open Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Block>
        )*/}

        <BlockHead size="lg" className="mb-5">
          <BlockHeadContent>
            <Card className="border-0 shadow-lg hero-card" style={{ 
              borderRadius: '30px', 
              background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #e2e8f0 100%)',
              overflow: 'hidden',
              position: 'relative',
              padding: '0'
            }}>
              {/* Background decorative elements */}
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '200px',
                height: '200px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.08), rgba(16, 185, 129, 0.08))',
                borderRadius: '50%',
                filter: 'blur(60px)',
                zIndex: 1
              }}></div>
              <div style={{
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '150px',
                height: '150px',
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08), rgba(139, 92, 246, 0.08))',
                borderRadius: '50%',
                filter: 'blur(50px)',
                zIndex: 1
              }}></div>
              
              <CardBody className="p-5" style={{ position: 'relative', zIndex: 2 }}>
                <Row className="align-items-center">
                  {/* Left Side - Large SSB Logo */}
                  <Col lg={4} md={12} className="text-center text-lg-start mb-4 mb-lg-0">
                    <div className="ssb-logo" style={{
                      width: '260px',
                      height: '260px',
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 25px 60px rgba(59, 130, 246, 0.4)',
                      border: '7px solid rgba(255,255,255,0.9)',
                      position: 'relative',
                      padding: '35px',
                      margin: '0 auto',
                      transition: 'all 0.3s ease'
                    }}>
                      <img 
                        src={LogoSSB} 
                        alt="SSB Logo" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          filter: 'brightness(1) contrast(1.1)'
                        }}
                      />
                      {/* Floating accent icon */}
                      <div style={{
                        position: 'absolute',
                        bottom: '18px',
                        right: '18px',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #10b981 0%, #047857 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 20px rgba(16, 185, 129, 0.4)',
                        border: '4px solid rgba(255,255,255,0.9)'
                      }}>
                        <Icon name="trending-up" className="text-white" style={{ 
                          fontSize: '1.7rem', 
                          filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                        }} />
                      </div>
                    </div>
                  </Col>
                  
                  {/* Right Side - Welcome Text */}
                  <Col lg={8} md={12}>
                    <div className="text-center text-lg-start">
                      <BlockTitle tag="h1" className="fw-light mb-4" style={{
                        fontSize: '2.2rem',
                        background: 'linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        letterSpacing: '-0.02em',
                        lineHeight: '1.1',
                        fontWeight: '300'
                      }}>
                        Welcome to <strong style={{
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          fontWeight: '700'
                        }}>Metrics</strong> Platform
                      </BlockTitle>
                      
                      <div style={{
                        padding: '30px 0',
                        marginTop: '20px'
                      }}>
                        <p className="lead mb-3" style={{
                          fontSize: '1.2rem',
                          color: '#475569',
                          fontWeight: '600',
                          lineHeight: '1.6',
                          marginBottom: '15px'
                        }}>
                          {rolMetric === 'appo' 
                            ? 'Manage Isotanks and Cylinders with Precision'
                            : 'Your comprehensive solution for import and export management'
                          }
                        </p>
                        <p style={{
                          fontSize: '1rem',
                          color: '#64748b',
                          fontWeight: '400',
                          lineHeight: '1.7',
                          maxWidth: '580px',
                          margin: '0 auto'
                        }}>
                          {rolMetric === 'appo'
                            ? 'Control isotank cleaning operations, track temporary storage inventory, manage shipper-owned equipment, handle temporary admissions, and streamline communications across all logistics channels.'
                            : 'Streamline your operations with advanced analytics, real-time tracking, and automated reporting tools designed for modern trade management.'
                          }
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </BlockHeadContent>
        </BlockHead>


        {/* Main Features Carousel Section */}
        <Block className="mb-5">
          <div className="carousel-container shadow-lg" style={{ 
            overflow: 'visible', 
            background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
            padding: '20px 30px 25px 30px',
            minHeight: '580px',
            borderRadius: '25px'
          }}>
            {/* Header inside carousel */}
            <div className="text-start mb-4" style={{ 
              borderBottom: '3px solid transparent',
              borderImage: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 50%, #10b981 100%) 1',
              paddingBottom: '15px',
              position: 'relative'
            }}>
              <div className="d-flex align-items-center">
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '15px',
                  boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
                  border: '2px solid rgba(255,255,255,0.8)'
                }}>
                  <Icon name="layers" className="text-white" style={{ 
                    fontSize: '1.5rem', 
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' 
                  }} />
                </div>
                <div>
                  <h3 className="fw-bold mb-1" style={{
                    fontSize: '2.2rem',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.5px',
                    lineHeight: '1.2'
                  }}>
                    System Modules
                  </h3>
                  <p className="mb-0" style={{
                    fontSize: '1.1rem',
                    fontWeight: '500',
                    color: '#64748b',
                    letterSpacing: '0.3px'
                  }}>
                    Explore our comprehensive suite of management tools
                  </p>
                </div>
              </div>
            </div>

            {systemFeatures.length === 0 ? (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: '300px', color: '#94a3b8' }}>
                <Icon name="lock-alt" style={{ fontSize: '3.5rem', marginBottom: '1rem' }} />
                <h5 style={{ color: '#64748b', fontWeight: '600' }}>No modules available</h5>
                <p style={{ color: '#94a3b8', fontSize: '1rem' }}>Your current role does not have access to any module.</p>
              </div>
            ) : (
              <Carousel
                activeIndex={activeIndex}
                next={next}
                previous={previous}
                className="custom-carousel"
                fade={false}
                interval={false}
                pause="hover"
              >
                {slides}
                {maxSlides > 1 && activeIndex > 0 && <CarouselControl direction="prev" directionText="Previous" onClickHandler={previous} />}
                {maxSlides > 1 && activeIndex < maxSlides - 1 && <CarouselControl direction="next" directionText="Next" onClickHandler={next} />}
              </Carousel>
            )}
          </div>
        </Block>

        {/* Additional Information Section */}
        <Block>
          <Row className="g-4 justify-content-center">
            <Col lg={12} xl={12}>
              <Card className="border-0 shadow-lg platform-features-card" style={{ 
                borderRadius: '25px', 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                overflow: 'hidden',
                position: 'relative'
              }}>
                <CardBody className="p-5 text-white" style={{ position: 'relative', zIndex: 2 }}>
                  <div className="text-start mb-4">
                    <div className="d-flex align-items-center">
                      <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: '20px',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <Icon name="award" style={{ fontSize: '2rem', opacity: 0.9 }} />
                      </div>
                      <div>
                        <h4 className="mb-1 fw-bold" style={{ fontSize: '1.8rem' }}>Platform Features</h4>
                        <p className="mb-0 opacity-90" style={{ fontSize: '1.1rem', maxWidth: '700px' }}>
                          Comprehensive tools for managing your import and export operations with advanced analytics and automated reporting.
                        </p>
                      </div>
                    </div>
                  </div>
                  <Row className="g-4">
                    <Col sm={6}>
                      <div className="d-flex align-items-center p-4 rounded feature-item" style={{ 
                        background: 'rgba(255,255,255,0.25)', 
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.4)',
                        borderRadius: '15px',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '15px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                        }}>
                          <Icon name="shield-check" className="text-white" style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ fontSize: '1.1rem' }}>Secure & Compliant</h6>
                          <small className="opacity-75">Enterprise-grade security</small>
                        </div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex align-items-center p-4 rounded feature-item" style={{ 
                        background: 'rgba(255,255,255,0.25)', 
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.4)',
                        borderRadius: '15px',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '15px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                          transition: 'all 0.3s ease'
                        }}>
                          <Icon name="reload" className="text-white" style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ fontSize: '1.1rem' }}>Real-time Updates</h6>
                          <small className="opacity-75">Live data synchronization</small>
                        </div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex align-items-center p-4 rounded feature-item" style={{ 
                        background: 'rgba(255,255,255,0.25)', 
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.4)',
                        borderRadius: '15px',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '15px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 4px 15px rgba(245, 158, 11, 0.3)',
                          transition: 'all 0.3s ease'
                        }}>
                          <Icon name="users" className="text-white" style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ fontSize: '1.1rem' }}>Multi-user Support</h6>
                          <small className="opacity-75">Collaborative workspace</small>
                        </div>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="d-flex align-items-center p-4 rounded feature-item" style={{ 
                        background: 'rgba(255,255,255,0.25)', 
                        backdropFilter: 'blur(10px)',
                        border: '2px solid rgba(255,255,255,0.4)',
                        borderRadius: '15px',
                        transition: 'all 0.3s ease'
                      }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '15px',
                          border: '2px solid rgba(255,255,255,0.3)',
                          boxShadow: '0 4px 15px rgba(139, 92, 246, 0.3)',
                          transition: 'all 0.3s ease'
                        }}>
                          <Icon name="bar-chart" className="text-white" style={{ fontSize: '1.5rem', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
                        </div>
                        <div>
                          <h6 className="mb-1 fw-semibold" style={{ fontSize: '1.1rem' }}>Advanced Analytics</h6>
                          <small className="opacity-75">Comprehensive reporting</small>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default MetricsHome;
